import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MdOutlineAddCard } from "react-icons/md";
import debounce from "lodash/debounce";
import useApi from "../hook/useApi";
import { useAuth } from "../context/AuthContext";
import Paggination from "./Paggination";
import Loader from "./Loader/Loader";
import { PAGE_SIZE_OPTIONS } from "../constants/pageSizeOptions";
import { formatCurrency, formatDateFull } from "../utils/helper";
import LedgerModal from "./Accounts/LedgerModal";
import SearchableSelect from "./Common/SearchableSelect";

const UsersListLayer = () => {
  const { AsyncGetApiCall, AsyncPatchApiCall } =
    useApi();
  const navigate = useNavigate();
  const [query, setQuery] = useState({
    isPagination: true,
    pageNo: 1,
    limitVal: 50,
    search: "",
    status: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionTooltip, setSessionTooltip] = useState({ visible: false, x: 0, y: 0, sessions: [] });

  const setPageNo = (pageNo) => {
    setQuery((prev) => ({
      ...prev,
      pageNo,
    }));
  };

  const getAllUsersList = async () => {
    try {
      setLoading(true);
      const result = await AsyncGetApiCall("/users/list", query);

      if (result.success) {
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching users list:", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleDelete = async (id) => {
  //   if (!window.confirm("Delete this panel user? This cannot be undone.")) {
  //     return;
  //   }
  //   setLoading(true);
  //   const result = await AsyncDeleteApiCall(`/users/delete/${id}`, {});

  //   if (result?.errors) {
  //     toast.error(
  //       Array.isArray(result.errors) ? result.errors[0] : result.errors,
  //     );
  //   } else if (result?.success) {
  //     toast.success(result.message || "User deleted");
  //     getAllUsersList();
  //   } else {
  //     toast.error(result?.message || "Delete failed");
  //   }
  //   setLoading(false);
  // };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    [],
  );

  const handleSearchUser = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    getAllUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleLedgerClick = (user) => {
    setSelectedAccount({
      name: user.name,
      id: user.loginId,
      dbId: user._id,
    });
    setIsModalOpen(true);
  };

  const handleStatus = async (id, status) => {
    const { isConfirmed } = await Swal.fire({
      title: "Change Status?",
      text: `${status ? "Deactivate" : "Activate"} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Change it!",
      customClass: { popup: "swal-sm" },
    });

    if (!isConfirmed) {
      return;
    }
    setLoading(true);
    const payload = {
      id: id,
      isActive: status ? false : true,
    };
    const result = await AsyncPatchApiCall(`/users/update`, payload);
    if (result?.errors) {
      toast.error(
        Array.isArray(result?.errors) ? result?.errors[0] : result?.errors,
      );
    } else if (result?.success) {
      toast.success(result?.message || "User status changed");
      getAllUsersList();
    } else {
      toast.error(result?.message || "User status changed failed");
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      {/* Fixed-position session tooltip — always visible, never clipped by table overflow */}
      {sessionTooltip.visible && (
        <div
          style={{
            position: "fixed",
            top: sessionTooltip.y,
            left: sessionTooltip.x,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 300,
            maxHeight: "60vh",
            overflowY: "auto",
            fontSize: 11,
            pointerEvents: "none",
          }}
          onMouseLeave={() => setSessionTooltip((p) => ({ ...p, visible: false }))}
        >
          <div style={{ padding: "6px 14px 8px", fontWeight: 600, color: "#555", borderBottom: "1px solid #eee" }}>
            All Login Sessions ({sessionTooltip.sessions.length})
          </div>
          {sessionTooltip.sessions.map((s, i) => (
            <div key={i} style={{ padding: "6px 14px", borderBottom: "1px solid #f2f2f2" }}>
              <div style={{ color: "#555", marginTop: 2 }}><b>#{i + 1}</b>&nbsp; {s.loginAt}</div>
              <div style={{ color: "#888888ff", marginTop: 2 }}>{s.ip}&nbsp;|&nbsp;{s.browser}&nbsp;|&nbsp;{s.os}</div>
            </div>
          ))}
        </div>
      )}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <p className="text-md fw-medium text-secondary-light mb-0">
                Show
              </p>
              <SearchableSelect
                placeholder="Show"
                value={String(query.limitVal)}
                showSearch={false}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setQuery((prev) => ({
                    ...prev,
                    limitVal: value,
                    pageNo: 1,
                  }));
                }}
              />
            </div>
            <form className="navbar-search" onSubmit={handleFormSubmit}>
              <input
                type="text"
                className="bg-base h-40-px"
                name="search"
                placeholder="Search by login-ID, name, broker"
                onChange={handleSearchUser}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
            <p className="text-md fw-medium text-secondary-light mb-0">
              Status
            </p>
            <select
              name="status"
              id="status"
              className="form-select h-40-px"
              value={query.status}
              onChange={(e) => {
                const value = e.target.value;
                setQuery((prev) => ({ ...prev, status: value, pageNo: 1 }));
              }}
              style={{ width: "auto" }}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2"
            onClick={() => {
              console.log("clicked");
              navigate("/user-panel/create");
            }}
            disabled={loading}
          >
            <MdOutlineAddCard className="text-xl" />
            Add User
          </button>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Login ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Upline</th>
                  <th scope="col">Current Balance</th>
                  <th scope="col">Created By</th>
                  <th scope="col">Brokers</th>
                  <th scope="col">Login Info</th>
                  <th scope="col">Created at</th>
                  <th scope="col" className="text-center">Status</th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {data && data.data?.length > 0 ? (
                  data.data.map((user, index) => (
                    <tr key={user._id}>
                      <td>
                        {(data?.currentPage - 1) * query.limitVal + index + 1}
                      </td>
                      <td>
                        <span className="text-md text-secondary-light">
                          {user.loginId || "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className="text-md text-secondary-light">
                          {user.name || "N/A"}
                        </span>
                      </td>
                      <td>
                        {user.upline ? (
                          <div className="d-flex flex-column">
                            <span className="text-sm fw-semibold">{user.upline.name}</span>
                            <span className="text-xs text-secondary-light">{user.upline.loginId}</span>
                            <span className="text-xs text-secondary-light" style={{ fontSize: "10px", textTransform: "uppercase" }}>{user.upline.role}</span>
                          </div>
                        ) : (
                          <span className="text-secondary-light">—</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="text-md fw-medium text-secondary-light" title="Real Balance">
                            {formatCurrency(user.currentBalance) || "0.00"}
                          </span>
                          {(user.unsettledPnl !== 0 && user.unsettledPnl !== undefined) && (
                            <span
                              className={`text-xs fw-semibold ${user.unsettledPnl >= 0 ? "text-success-600" : "text-danger-600"}`}
                              title="Unsettled Amount (Running PnL + Brokerage)"
                            >
                              Unsettled: {formatCurrency(user.unsettledPnl)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-md text-secondary-light">
                          {user.createdByModel === "Admin" ? user.createdByModel : `${user.createdById?.masterName || user.createdById?.name} (${user.createdById?.loginId}) (${user.createdByModel}) ` || "-"}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm text-secondary-light">
                          {user.brokers?.length > 0
                            ? user.brokers.map((b) => b.name).join(" / ")
                            : user.brokerDetails?.length > 0
                              ? user.brokerDetails.map((b) => b.brokerId?.name || b.brokerId).join(" / ")
                              : "—"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-0" style={{ fontSize: "11px" }}>
                          <span className="fw-medium">{user.lastLoginIp || "No IP logged"}</span>
                          <span className="text-secondary-light">{user.lastLogin || "Never logged in"}</span>
                        </div>
                        {user.loginSessions?.length > 1 && (
                          <div className="d-inline-flex align-items-center mt-1">
                            <Icon
                              icon="mdi:information-outline"
                              style={{ fontSize: 15, color: "#2352c3", cursor: "pointer" }}
                              title={`${user.loginSessions.length} sessions — hover to view`}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                // Position so tooltip doesn't go off the right or bottom edge
                                const x = Math.min(rect.left, window.innerWidth - 320);
                                const y = rect.bottom + 6;
                                setSessionTooltip({ visible: true, x, y, sessions: user.loginSessions });
                              }}
                              onMouseLeave={() => setSessionTooltip((p) => ({ ...p, visible: false }))}
                            />
                          </div>
                        )}
                      </td>
                      <td>{formatDateFull(user.createdAt)}</td>
                      <td className="text-center">
                        <span
                          className={`${user.isActive
                            ? "bg-success-focus text-success-600"
                            : "bg-danger-focus text-danger-600"
                            } border px-16 py-4 radius-4 fw-medium text-sm cursor-pointer`}
                          onClick={() =>
                            handleStatus(user?._id, user?.isActive)
                          }
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-center flex-wrap">
                          <button
                            type="button"
                            className="ledger-action-btn w-40-px h-40-px"
                            onClick={() => handleLedgerClick(user)}
                            aria-label="View ledger"
                          >
                            <Icon icon="tabler:file-text" className="icon" />
                          </button>
                          <button
                            type="button"
                            title="View"
                            className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/view-user-profile/${user._id}`)
                            }
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon"
                            />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/edit-user-profile/${user._id}`)
                            }
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          {/* {!subAdminId && (
                            <button
                              type="button"
                              title="Delete"
                              className="bg-danger-focus text-danger-600 fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              onClick={() => handleDelete(user._id)}
                            >
                              <Icon
                                icon="mdi:delete-outline"
                                className="icon"
                              />
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paggination
            total={data?.total || 0}
            totalPages={data?.totalPages || 0}
            currentPage={data?.currentPage || 0}
            setPageNo={setPageNo}
            startFrom={(data?.currentPage - 1) * query.limitVal + 1}
            endTo={
              (data?.currentPage - 1) * query.limitVal +
              (data?.data?.length || 0)
            }
          />

          {selectedAccount && (
            <LedgerModal
              show={!!selectedAccount && isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setSelectedAccount(null);
              }}
              accountName={selectedAccount?.name ?? ""}
              accountId={selectedAccount?.id ?? ""}
              accountDbId={selectedAccount?.dbId}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UsersListLayer;
